import React, { Component } from "react";
import { toast } from "react-toastify";
import {
  getSalesDocumentRequestsService,
  getSalesDocumentRequestDetailService,
  uploadSalesDocumentsService,
  downloadSalesDocumentService,
} from "../../../services/deliveryRequest.service";
import EminevimLoading from "../shared/EminevimLoading";
import LoginPageAlert from "../Login/LoginPageAlert";

const PAGE_SIZE = 8;
const REJECTED_DOCUMENT_UPLOAD_NOTICE_STATUS_IDS = [28, 75];
const REJECTED_DOCUMENT_UPLOAD_NOTICE =
  "Hatalı dokümanlar için değiştirme yapıp kaydettiyseniz lütfen dikkate almayınız";
const MAX_FILE_SIZE = 8 * 1024 * 1024;
const ACCEPTED_TYPES = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
const ACCEPTED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png"];
const PLATE_LETTER_REGEX = /[A-HJ-NPR-Z]/;
const PLATE_FORMAT_REGEX = /^(0[1-9]|[1-7][0-9]|80|81)\s[A-HJ-NPR-Z]{1,3}\s\d{2,4}$/;

const formatPlateInput = (value) => {
  const cleaned = (value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!cleaned) return "";

  const cityDigits = cleaned.slice(0, 2).replace(/[^0-9]/g, "");
  const remainder = cleaned.slice(cityDigits.length);

  let letters = "";
  let numbers = "";

  for (let index = 0; index < remainder.length; index += 1) {
    const char = remainder[index];
    if (numbers.length === 0 && letters.length < 3 && PLATE_LETTER_REGEX.test(char)) {
      letters += char;
    } else if (/\d/.test(char) && numbers.length < 4) {
      numbers += char;
    }
  }

  let formatted = cityDigits;
  if (letters) {
    formatted = formatted ? `${formatted} ${letters}` : letters;
  }
  if (numbers) {
    formatted = formatted ? `${formatted} ${numbers}` : numbers;
  }

  return formatted;
};

const isValidPlateFormat = (value) => PLATE_FORMAT_REGEX.test((value || "").trim().toUpperCase());

const REGISTRATION_SERIAL_FORMAT_REGEX = /^[A-Z]{2}\s\d{6}$/;

const formatRegistrationSerialInput = (value) => {
  const cleaned = (value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!cleaned) return "";

  const letters = cleaned.slice(0, 2).replace(/[^A-Z]/g, "");
  const numbers = cleaned.slice(letters.length).replace(/[^0-9]/g, "").slice(0, 6);

  if (!letters && !numbers) return "";
  if (!numbers) return letters;
  return letters ? `${letters} ${numbers}` : numbers;
};

const isValidRegistrationSerialFormat = (value) =>
  REGISTRATION_SERIAL_FORMAT_REGEX.test((value || "").trim().toUpperCase());

const normalizeRegistrationSerial = (value) => (value || "").replace(/\s/g, "").toUpperCase();

const SALES_DOCUMENT_DEFINITIONS = [
  {
    type: "SALES_AGREEMENT",
    name: "Devir Sonrası Araç Satış Sözleşmesi",
    field: "salesContractFile",
    ref: "salesContractInputRef",
  },
  {
    type: "VEHICLE_REGISTRATION",
    name: "Devir Sonrası Araç Ruhsat Belgesi",
    field: "registrationFile",
    ref: "registrationInputRef",
  },
];

class SalesDocumentUploadTab extends Component {
  constructor(props) {
    super(props);
    this.salesContractInputRef = React.createRef();
    this.registrationInputRef = React.createRef();

    this.state = {
      hasLoaded: false,
      requests: [],
      isLoading: false,
      currentPage: 1,
      showDetailModal: false,
      showUploadModal: false,
      detailLoading: false,
      uploadDetailLoading: false,
      uploadSubmitting: false,
      selectedRequest: null,
      requestDetail: null,
      uploadRequestDetail: null,
      uploadExistingDocuments: {},
      uploadForm: {
        changePlate: false,
        newPlate: "",
        buyerRegistrationSerialNumber: "",
        salesContractFile: null,
        registrationFile: null,
      },
      dragOverField: null,
      listMeta: null,
    };
  }

  parseApiResult = (result) => {
    if (typeof result === "string") {
      try {
        return JSON.parse(result);
      } catch (error) {
        return null;
      }
    }
    return result;
  };

  getField = (obj, ...keys) => {
    if (!obj) return undefined;
    for (const key of keys) {
      if (obj[key] !== undefined && obj[key] !== null) return obj[key];
    }
    return undefined;
  };

  isZeroVehicleRequest = (request) => this.getField(request, "isZeroVehicle", "IsZeroVehicle") === true;

  isNewPlateRequired = (request, uploadForm) =>
    uploadForm?.changePlate === true || this.isZeroVehicleRequest(request);

  findDetailDocument = (detail, documentType) => {
    const documents = this.normalizeDocuments(detail);
    return documents.find(
      (document) => this.getField(document, "documentType", "DocumentType") === documentType
    );
  };

  getDocumentRowKey = (document, index) => {
    const documentId = this.getField(document, "documentId", "DocumentId");
    const documentType = this.getField(document, "documentType", "DocumentType");
    const source = this.getField(document, "source", "Source");
    const fileName = this.getField(document, "fileName", "FileName");
    const uploadedDate = this.getField(document, "uploadedDate", "UploadedDate");

    const identity = [
      documentType || "type",
      source || "source",
      documentId ?? "id",
      fileName || "file",
      uploadedDate || "date",
      index,
    ].join("-");

    return `sales-doc-${identity}`;
  };

  buildUploadDefinitionFromDocument = (document, index) => {
    const documentType = this.getField(document, "documentType", "DocumentType");
    const baseDefinition = SALES_DOCUMENT_DEFINITIONS.find((definition) => definition.type === documentType);

    if (!baseDefinition) return null;

    return {
      ...baseDefinition,
      name: this.getField(document, "documentName", "DocumentName") || baseDefinition.name,
      detailDocument: document,
      canUpload: this.getField(document, "canUpload", "CanUpload") !== false,
      rowKey: this.getDocumentRowKey(document, index),
    };
  };

  getUploadDocumentDefinitions = (request, detail = null) => {
    const allDocuments = detail ? this.normalizeDocuments(detail) : [];
    const documents = this.filterDocumentsForUploadModal(allDocuments);

    if (allDocuments.length > 0) {
      return documents
        .map((document, index) => this.buildUploadDefinitionFromDocument(document, index))
        .filter(Boolean);
    }

    return SALES_DOCUMENT_DEFINITIONS.map((definition, index) => ({
      ...definition,
      detailDocument: null,
      canUpload: true,
      rowKey: `sales-doc-fallback-${definition.type}-${index}`,
    }));
  };

  normalizeRequests = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    const requests = this.getField(data, "requests", "Requests");
    if (Array.isArray(requests)) return requests;
    if (Array.isArray(data.list)) return data.list;
    if (Array.isArray(data.items)) return data.items;
    if (this.getField(data, "requestId", "RequestId")) return [data];
    return [];
  };

  getListMeta = (data) => ({
    isActive: this.getField(data, "isActive", "IsActive"),
    message: this.getField(data, "message", "Message"),
    activeRequestCount: this.getField(data, "activeRequestCount", "ActiveRequestCount"),
  });

  normalizeDocuments = (data) => {
    const docs = this.getField(data, "documents", "Documents");
    return Array.isArray(docs) ? docs : [];
  };

  getDocumentSource = (document) =>
    String(this.getField(document, "source", "Source") || "").trim().toUpperCase();

  isPortalSourceDocument = (document) => this.getDocumentSource(document) === "PORTAL";

  filterDocumentsForUploadModal = (documents) =>
    (documents || []).filter((document) => !this.isPortalSourceDocument(document));

  isUploadedSalesDocument = (document) => {
    if (!document) return false;

    const documentId = Number(this.getField(document, "documentId", "DocumentId"));
    return Number.isFinite(documentId) && documentId > 0;
  };

  getExistingUploadDocument = (definition) => {
    const { uploadExistingDocuments } = this.state;
    const candidate =
      uploadExistingDocuments[definition.rowKey] || definition.detailDocument || null;

    return this.isUploadedSalesDocument(candidate) ? candidate : null;
  };

  formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  getRequestStatusClass = (status) => {
    const value = (status || "").toLowerCase();
    if (value.includes("tamamland")) return "t-salesDocStatus--success";
    if (value.includes("bekl")) return "t-salesDocStatus--waiting";
    if (value.includes("incelen")) return "t-salesDocStatus--review";
    if (value.includes("hatal")) return "t-salesDocStatus--error";
    if (value.includes("devir")) return "t-salesDocStatus--waiting";
    return "t-salesDocStatus--default";
  };

  normalizeStatusColorCode = (colorCode) => {
    if (!colorCode || typeof colorCode !== "string") return null;

    let normalized = colorCode.trim();
    if (!normalized.startsWith("#")) {
      normalized = `#${normalized}`;
    }

    if (/^#[0-9a-fA-F]{3}$/.test(normalized)) {
      normalized = `#${normalized[1]}${normalized[1]}${normalized[2]}${normalized[2]}${normalized[3]}${normalized[3]}`;
    }

    return /^#[0-9a-fA-F]{6}$/.test(normalized) ? normalized.toLowerCase() : null;
  };

  hexToRgba = (hex, alpha) => {
    const normalized = this.normalizeStatusColorCode(hex);
    if (!normalized) return null;

    const value = normalized.replace("#", "");
    const red = parseInt(value.slice(0, 2), 16);
    const green = parseInt(value.slice(2, 4), 16);
    const blue = parseInt(value.slice(4, 6), 16);

    return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
  };

  getStatusBadgeStyle = (statusColorCode) => {
    const color = this.normalizeStatusColorCode(statusColorCode);
    if (!color) return null;

    return {
      color,
      backgroundColor: this.hexToRgba(color, 0.12),
      borderColor: this.hexToRgba(color, 0.24),
    };
  };

  hasStatusText = (status) => {
    if (status == null) return false;
    if (typeof status === "string") return status.trim().length > 0;
    return true;
  };

  renderStatusBadge = (status, statusColorCode, options = {}) => {
    const { fallback = "-" } = options;

    if (!this.hasStatusText(status)) {
      return fallback;
    }

    const customStyle = this.getStatusBadgeStyle(statusColorCode);

    return (
      <span
        className={`t-salesDocStatus ${
          customStyle ? "t-salesDocStatus--custom" : this.getRequestStatusClass(status)
        }`}
        style={customStyle || undefined}
      >
        {status}
      </span>
    );
  };

  getRequestStatusText = (request) =>
    this.getField(request, "requestStatus", "RequestStatus", "statusName", "StatusName") || "-";

  getRequestStatusColorCode = (detail, request) =>
    this.getField(detail, "statusColorCode", "StatusColorCode") ||
    this.getField(request, "statusColorCode", "StatusColorCode");

  getRequestStatusId = (detail, request) =>
    Number(
      this.getField(detail, "requestStatusId", "RequestStatusId") ??
        this.getField(request, "requestStatusId", "RequestStatusId")
    );

  isVehicleInfoLocked = (detail, request) => this.getRequestStatusId(detail, request) === 75;

  shouldShowRejectedDocumentUploadNotice = (detail, request) =>
    REJECTED_DOCUMENT_UPLOAD_NOTICE_STATUS_IDS.includes(this.getRequestStatusId(detail, request));

  fetchRequestDetail = (memberId, requestId) =>
    getSalesDocumentRequestDetailService(memberId, requestId).then((result) => {
      const parsed = this.parseApiResult(result);
      if (parsed?.success || parsed?.Success) {
        return parsed.data || parsed.Data || null;
      }

      throw new Error(parsed?.message || parsed?.Message || "Talep detayı yüklenemedi");
    });

  mergeRequestWithDetail = (request, detail) => {
    if (!detail) return request;

    return {
      ...request,
      requestId: this.getField(detail, "requestId", "RequestId") ?? this.getField(request, "requestId", "RequestId"),
      plate: this.getField(request, "plate", "Plate"),
      requestStatus:
        this.getField(detail, "requestStatus", "RequestStatus") ||
        this.getField(request, "requestStatus", "RequestStatus", "statusName", "StatusName"),
      isZeroVehicle:
        this.getField(detail, "isZeroVehicle", "IsZeroVehicle") ??
        this.getField(request, "isZeroVehicle", "IsZeroVehicle"),
      createdDate: this.getField(request, "createdDate", "CreatedDate"),
    };
  };

  buildUploadFormFromDetail = (detail) => {
    const isZeroVehicle = this.isZeroVehicleRequest(detail);
    const changePlateFromDetail = this.getField(detail, "changePlate", "ChangePlate") === true;
    const changePlate = isZeroVehicle ? true : changePlateFromDetail;
    const newPlateRaw = this.getField(detail, "newPlate", "NewPlate") || "";
    const buyerSerialRaw =
      this.getField(detail, "buyerRegistrationSerialNumber", "BuyerRegistrationSerialNumber") || "";

    return {
      changePlate,
      newPlate: changePlate ? formatPlateInput(newPlateRaw) : "",
      buyerRegistrationSerialNumber: formatRegistrationSerialInput(buyerSerialRaw),
      salesContractFile: null,
      registrationFile: null,
    };
  };

  buildExistingDocumentsMap = (detail, request) => {
    const map = {};

    for (const definition of this.getUploadDocumentDefinitions(request || detail, detail)) {
      const existingDocument = definition.detailDocument;

      if (this.isUploadedSalesDocument(existingDocument)) {
        map[definition.rowKey] = existingDocument;
      }
    }

    return map;
  };

  getUploadDocumentDisplayName = (definition) => {
    const { uploadForm } = this.state;
    const selectedFile = uploadForm[definition.field];
    const existingDocument = this.getExistingUploadDocument(definition);

    if (selectedFile) {
      return selectedFile.name;
    }

    if (existingDocument) {
      return (
        this.getField(existingDocument, "fileName", "FileName") ||
        this.getField(existingDocument, "documentName", "DocumentName") ||
        definition.name
      );
    }

    return null;
  };

  hasUploadDocument = (definition) => {
    const { uploadForm } = this.state;
    return Boolean(uploadForm[definition.field] || this.getExistingUploadDocument(definition));
  };

  loadRequests = () => {
    const { memberId } = this.props;
    if (!memberId) return;

    this.setState({ isLoading: true });
    getSalesDocumentRequestsService(memberId)
      .then((result) => {
        const parsed = this.parseApiResult(result);
        if (parsed?.success || parsed?.Success) {
          const responseData = parsed.data || parsed.Data || {};
          const listMeta = this.getListMeta(responseData);
          const requests = this.normalizeRequests(responseData);
          this.setState({ requests, isLoading: false, hasLoaded: true, currentPage: 1, listMeta });
          if (this.props.onListLoaded) {
            this.props.onListLoaded({
              isActive: listMeta.isActive !== false,
              message: listMeta.message,
              requestCount: requests.length,
            });
          }
          if (listMeta.isActive === false && listMeta.message) {
            toast.info(listMeta.message);
          }
        } else {
          this.setState({ requests: [], isLoading: false, hasLoaded: true, listMeta: null });
          if (this.props.onListLoaded) {
            this.props.onListLoaded({ isActive: false, message: parsed?.message || parsed?.Message, requestCount: 0 });
          }
          toast.error(parsed?.message || parsed?.Message || "Talep listesi yüklenemedi");
        }
      })
      .catch(() => {
        this.setState({ requests: [], isLoading: false, hasLoaded: true });
        toast.error("Talep listesi yüklenemedi");
      });
  };

  openDetailModal = (request) => {
    const { memberId } = this.props;
    const requestId = this.getField(request, "requestId", "RequestId");
    if (!requestId) return;

    this.setState({
      showDetailModal: true,
      selectedRequest: request,
      requestDetail: null,
      detailLoading: true,
    });

    this.fetchRequestDetail(memberId, requestId)
      .then((requestDetail) => {
        this.setState({
          requestDetail,
          detailLoading: false,
        });
      })
      .catch((error) => {
        this.setState({ detailLoading: false, showDetailModal: false });
        toast.error(error?.message || "Talep detayı yüklenemedi");
      });
  };

  closeDetailModal = () => {
    this.setState({
      showDetailModal: false,
      selectedRequest: null,
      requestDetail: null,
      detailLoading: false,
    });
  };

  openUploadModal = (request) => {
    const { memberId } = this.props;
    const requestId = this.getField(request, "requestId", "RequestId");
    const canUpload = this.getField(request, "canUploadDocument", "CanUploadDocument");

    if (canUpload === false) {
      toast.warning("Bu talep için doküman yükleme işlemi yapılamaz");
      return;
    }

    if (!requestId) return;

    this.setState({
      showUploadModal: true,
      selectedRequest: request,
      uploadRequestDetail: null,
      uploadExistingDocuments: {},
      uploadDetailLoading: true,
      uploadSubmitting: false,
      dragOverField: null,
      uploadForm: {
        changePlate: false,
        newPlate: "",
        buyerRegistrationSerialNumber: "",
        salesContractFile: null,
        registrationFile: null,
      },
    });

    this.fetchRequestDetail(memberId, requestId)
      .then((requestDetail) => {
        const mergedRequest = this.mergeRequestWithDetail(request, requestDetail);

        this.setState({
          selectedRequest: mergedRequest,
          uploadRequestDetail: requestDetail,
          uploadExistingDocuments: this.buildExistingDocumentsMap(requestDetail, mergedRequest),
          uploadForm: this.buildUploadFormFromDetail(requestDetail),
          uploadDetailLoading: false,
        });
      })
      .catch((error) => {
        this.setState({
          showUploadModal: false,
          selectedRequest: null,
          uploadRequestDetail: null,
          uploadExistingDocuments: {},
          uploadDetailLoading: false,
        });
        toast.error(error?.message || "Talep detayı yüklenemedi");
      });
  };

  closeUploadModal = () => {
    this.setState({
      showUploadModal: false,
      selectedRequest: null,
      uploadRequestDetail: null,
      uploadExistingDocuments: {},
      uploadDetailLoading: false,
      uploadSubmitting: false,
      dragOverField: null,
      uploadForm: {
        changePlate: false,
        newPlate: "",
        buyerRegistrationSerialNumber: "",
        salesContractFile: null,
        registrationFile: null,
      },
    });
  };

  handleUploadFormChange = (field, value) => {
    const { selectedRequest, uploadRequestDetail } = this.state;
    const requestContext = uploadRequestDetail || selectedRequest;

    if (this.isVehicleInfoLocked(requestContext, selectedRequest)) {
      if (field === "changePlate" || field === "newPlate" || field === "buyerRegistrationSerialNumber") {
        return;
      }
    }

    if (field === "changePlate" && value === false && this.isZeroVehicleRequest(requestContext)) {
      return;
    }

    let nextValue = value;
    if (field === "newPlate") {
      nextValue = formatPlateInput(value);
    } else if (field === "buyerRegistrationSerialNumber") {
      nextValue = formatRegistrationSerialInput(value);
    }

    this.setState((prev) => ({
      uploadForm: {
        ...prev.uploadForm,
        [field]: nextValue,
        ...(field === "changePlate" && !value ? { newPlate: "" } : {}),
      },
    }));
  };

  validateSelectedFile = (file) => {
    if (!file) return "Dosya seçilmedi";
    if (file.size > MAX_FILE_SIZE) return "Dosya boyutu en fazla 8 MB olabilir";
    if (!this.isAcceptedFileType(file)) return "Yalnızca PDF, JPG ve PNG dosyaları yüklenebilir";
    return null;
  };

  isAcceptedFileType = (file) => {
    if (ACCEPTED_TYPES.includes(file.type)) return true;
    const fileName = (file.name || "").toLowerCase();
    return ACCEPTED_EXTENSIONS.some((extension) => fileName.endsWith(extension));
  };

  handleDropzoneDragEnter = (field, event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ dragOverField: field });
  };

  handleDropzoneDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  handleDropzoneDragLeave = (field, event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!event.currentTarget.contains(event.relatedTarget)) {
      this.setState((prev) => ({
        dragOverField: prev.dragOverField === field ? null : prev.dragOverField,
      }));
    }
  };

  handleDropzoneDrop = (field, event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ dragOverField: null });

    if (this.state.uploadSubmitting || this.state.uploadDetailLoading) return;

    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.handleFileSelect(field, file);
    }
  };

  handleDropzoneClick = (inputRef) => {
    if (this.state.uploadSubmitting || this.state.uploadDetailLoading) return;
    const input = inputRef.current;
    if (input) {
      input.click();
    }
  };

  handleFileSelect = (field, file) => {
    const error = this.validateSelectedFile(file);
    if (error) {
      toast.error(error);
      return;
    }
    this.setState((prev) => ({
      uploadForm: {
        ...prev.uploadForm,
        [field]: file,
      },
    }));
  };

  handleFileInputChange = (field, event) => {
    const file = event.target.files?.[0];
    if (file) {
      this.handleFileSelect(field, file);
    }
    event.target.value = "";
  };

  clearSelectedFile = (field) => {
    this.setState((prev) => ({
      uploadForm: {
        ...prev.uploadForm,
        [field]: null,
      },
    }));
  };

  getFileExtension = (fileName) => {
    const parts = (fileName || "").split(".");
    if (parts.length < 2) return "";
    return parts.pop().toLowerCase();
  };

  readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result || "";
        const base64 = String(result).includes(",") ? String(result).split(",")[1] : String(result);
        resolve(base64);
      };
      reader.onerror = () => reject(new Error("Dosya okunamadı"));
      reader.readAsDataURL(file);
    });

  buildUploadPayload = async () => {
    const { memberId } = this.props;
    const { selectedRequest, uploadForm, uploadRequestDetail } = this.state;
    const isZeroVehicle = this.isZeroVehicleRequest(uploadRequestDetail || selectedRequest);
    const documents = [];

    for (const definition of this.getUploadDocumentDefinitions(selectedRequest, uploadRequestDetail)) {
      if (definition.canUpload === false) continue;

      const file = uploadForm[definition.field];
      if (!file || typeof file !== "object" || !file.name) continue;

      const fileExtension = this.getFileExtension(file.name);
      const fileContent = await this.readFileAsBase64(file);
      documents.push({
        documentType: definition.type,
        fileName: file.name,
        fileExtension,
        fileContent,
      });
    }

    return {
      memberId: Number(memberId),
      requestId: Number(this.getField(selectedRequest, "requestId", "RequestId")),
      changePlate: isZeroVehicle ? true : uploadForm.changePlate,
      newPlate: uploadForm.changePlate || isZeroVehicle ? uploadForm.newPlate.trim().toUpperCase() : null,
      buyerRegistrationSerialNumber: normalizeRegistrationSerial(uploadForm.buyerRegistrationSerialNumber),
      documents,
    };
  };

  submitUpload = async () => {
    const { uploadForm, selectedRequest, uploadRequestDetail } = this.state;
    const requiredDocuments = this.getUploadDocumentDefinitions(selectedRequest, uploadRequestDetail);
    const uploadableDocuments = requiredDocuments.filter((definition) => definition.canUpload !== false);

    if (!uploadableDocuments.length) {
      toast.warning("Bu talep için yüklenebilir doküman bulunmuyor");
      return;
    }

    if (!uploadForm.buyerRegistrationSerialNumber.trim()) {
      toast.error("Alıcı ruhsat seri belge numarası zorunludur");
      return;
    }

    if (!isValidRegistrationSerialFormat(uploadForm.buyerRegistrationSerialNumber)) {
      toast.error("Geçerli bir ruhsat seri belge numarası giriniz (Örn. AA 123456)");
      return;
    }

    const isNewPlateRequired = this.isNewPlateRequired(uploadRequestDetail || selectedRequest, uploadForm);

    if (isNewPlateRequired && !uploadForm.newPlate.trim()) {
      toast.warning("Plaka değişikliği Evet seçildiğinde yeni plaka zorunludur");
      return;
    }

    if (isNewPlateRequired && !isValidPlateFormat(uploadForm.newPlate)) {
      toast.error("Geçerli bir plaka formatı giriniz (Örn. 34 ABC 123)");
      return;
    }

    for (const definition of uploadableDocuments) {
      const hasNewFile = Boolean(uploadForm[definition.field]);
      const hasExistingDocument = Boolean(this.getExistingUploadDocument(definition));

      if (!hasNewFile && !hasExistingDocument) {
        toast.error("Zorunlu dokümanların tamamını yükleyiniz");
        return;
      }

      if (hasNewFile) {
        const fileError = this.validateSelectedFile(uploadForm[definition.field]);
        if (fileError) {
          toast.error(fileError);
          return;
        }
      }
    }

    this.setState({ uploadSubmitting: true });

    try {
      const payload = await this.buildUploadPayload();
      if (!payload.documents.length) {
        throw new Error("Değiştirmek istediğiniz dokümanları seçiniz");
      }

      const result = await uploadSalesDocumentsService(payload);
      const parsed = this.parseApiResult(result);
      if (!(parsed?.success || parsed?.Success)) {
        throw new Error(parsed?.message || parsed?.Message || "Doküman yüklenemedi");
      }

      toast.success("Dokümanlar başarıyla kaydedildi ve gönderildi");
      this.closeUploadModal();
      this.loadRequests();
    } catch (error) {
      toast.error(typeof error === "string" ? error : error?.message || "Doküman yüklenemedi");
      this.setState({ uploadSubmitting: false });
    }
  };

  isBrowserViewableDocument = (mimeType, fileName) => {
    const normalizedMime = (mimeType || "").toLowerCase();

    if (
      normalizedMime === "application/pdf" ||
      normalizedMime === "image/jpeg" ||
      normalizedMime === "image/jpg" ||
      normalizedMime === "image/png"
    ) {
      return true;
    }

    const extension = (fileName || "").split(".").pop()?.toLowerCase();
    return ["pdf", "jpg", "jpeg", "png"].includes(extension);
  };

  openDocumentFile = (blob, fileName, mimeType) => {
    const fileURL = window.URL.createObjectURL(blob);

    if (this.isBrowserViewableDocument(mimeType, fileName)) {
      window.open(fileURL, "_blank");
      return;
    }

    const link = document.createElement("a");
    link.href = fileURL;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(fileURL);
  };

  downloadDocument = (documentItem) => {
    const { memberId } = this.props;
    const { selectedRequest } = this.state;
    const documentId = this.getField(documentItem, "documentId", "DocumentId");
    const requestId =
      this.getField(selectedRequest, "requestId", "RequestId") ||
      this.getField(documentItem, "requestId", "RequestId");
    const fileName = this.getField(documentItem, "fileName", "FileName") || "dokuman";
    const canView = this.getField(documentItem, "canView", "CanView");

    if (canView === false || !documentId) {
      toast.warning("Bu doküman görüntülenemez");
      return;
    }

    downloadSalesDocumentService({
      memberId: Number(memberId),
      requestId: Number(requestId),
      documentId: Number(documentId),
    })
      .then((result) => {
        const parsed = this.parseApiResult(result);
        if (!(parsed?.success || parsed?.Success)) {
          toast.error(parsed?.message || parsed?.Message || "Doküman indirilemedi");
          return;
        }

        const responseData = parsed.data || parsed.Data || {};
        const fileBase64 =
          this.getField(responseData, "fileContent", "FileContent", "fileBase64", "FileBase64") || "";
        if (!fileBase64) {
          toast.error("Doküman içeriği alınamadı");
          return;
        }

        const mimeType = this.getField(responseData, "mimeType", "MimeType") || "application/octet-stream";
        const downloadName =
          this.getField(responseData, "fileName", "FileName") || fileName;
        const byteCharacters = atob(fileBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i += 1) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
        this.openDocumentFile(blob, downloadName, mimeType);
      })
      .catch(() => {
        toast.error("Doküman indirilemedi");
      });
  };

  renderLoading = (
    subtext = "Satış dokümanı talepleriniz hazırlanıyor, lütfen bekleyiniz."
  ) => (
    <EminevimLoading compact subtext={subtext} />
  );

  renderDetailButton = (request, canViewDetail) => (
    <button
      type="button"
      className="t-salesDocIconBtn"
      title="Detay"
      aria-label="Detay"
      onClick={() => this.openDetailModal(request)}
      disabled={canViewDetail === false}
    >
      <i className="t-icon-remove_red_eye" aria-hidden="true" />
      <span className="t-salesDocIconBtn-label">Detay</span>
    </button>
  );

  renderUploadButton = (request, canUploadDocument) => (
    <button
      type="button"
      className="t-salesDocIconBtn"
      title="Yükle"
      aria-label="Yükle"
      onClick={() => this.openUploadModal(request)}
      disabled={canUploadDocument === false}
    >
      <i className="t-icon-file_upload" aria-hidden="true" />
      <span className="t-salesDocIconBtn-label">Yükle</span>
    </button>
  );

  renderRequestList = (pageItems, listMeta) => {
    if (pageItems.length === 0) {
      return (
        <div className="t-salesDocDocumentsEmpty">
          {listMeta?.message || "Satış dokümanı talebi bulunmuyor."}
        </div>
      );
    }

    return (
      <>
        <div className="t-myDemands-desktopOnly t-salesDocDocumentsTableWrap">
          <table className="table table-sm t-tablo t-salesDocTable">
            <thead>
              <tr>
                <th>Talep ID</th>
                <th>Plaka</th>
                <th>Talep Durum</th>
                <th className="text-center t-salesDocActionsCol">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((request, index) => {
                const requestId = this.getField(request, "requestId", "RequestId");
                const plate = this.getField(request, "plate", "Plate");
                const requestStatus = this.getRequestStatusText(request);
                const statusColorCode = this.getField(request, "statusColorCode", "StatusColorCode");
                const canViewDetail = this.getField(request, "canViewDetail", "CanViewDetail");
                const canUploadDocument = this.getField(request, "canUploadDocument", "CanUploadDocument");

                return (
                  <tr key={requestId || index}>
                    <td>{requestId || "-"}</td>
                    <td>{plate || "-"}</td>
                    <td>{this.renderStatusBadge(requestStatus, statusColorCode)}</td>
                    <td className="text-center">
                      <div className="t-salesDocActionsCell">
                        {this.renderDetailButton(request, canViewDetail)}
                        {this.renderUploadButton(request, canUploadDocument)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="t-myDemands-mobileOnly t-myDemands-mobileList t-salesDocRequestMobileList">
          {pageItems.map((request, index) => {
            const requestId = this.getField(request, "requestId", "RequestId");
            const plate = this.getField(request, "plate", "Plate");
            const requestStatus = this.getRequestStatusText(request);
            const statusColorCode = this.getField(request, "statusColorCode", "StatusColorCode");
            const canViewDetail = this.getField(request, "canViewDetail", "CanViewDetail");
            const canUploadDocument = this.getField(request, "canUploadDocument", "CanUploadDocument");

            return (
              <article className="t-myDemands-mobileCard t-salesDocRequestCard" key={requestId || index}>
                <div className="t-myDemands-mobileField">
                  <span className="t-myDemands-mobileLabel">Talep ID</span>
                  <span className="t-myDemands-mobileValue">{requestId || "-"}</span>
                </div>
                <div className="t-myDemands-mobileField">
                  <span className="t-myDemands-mobileLabel">Plaka</span>
                  <span className="t-myDemands-mobileValue">{plate || "-"}</span>
                </div>
                <div className="t-myDemands-mobileField">
                  <span className="t-myDemands-mobileLabel">Talep Durum</span>
                  <span className="t-myDemands-mobileValue">
                    {this.renderStatusBadge(requestStatus, statusColorCode)}
                  </span>
                </div>
                <div className="t-myDemands-mobileActions t-salesDocRequestActions">
                  <button
                    type="button"
                    className="t-salesDocViewBtn t-salesDocViewBtn--mobile"
                    onClick={() => this.openDetailModal(request)}
                    disabled={canViewDetail === false}
                  >
                    <i className="t-icon-remove_red_eye" aria-hidden="true" />
                    Detay
                  </button>
                  <button
                    type="button"
                    className="t-salesDocViewBtn t-salesDocViewBtn--mobile t-salesDocUploadActionBtn"
                    onClick={() => this.openUploadModal(request)}
                    disabled={canUploadDocument === false}
                  >
                    <i className="t-icon-file_upload" aria-hidden="true" />
                    Yükle
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </>
    );
  };

  renderInfoGrid = (items) => (
    <div className="t-salesDocInfoGrid">
      {items.map((item) => (
        <div className="t-salesDocInfoItem" key={item.label}>
          <span className="t-salesDocInfoLabel">{item.label}</span>
          <span className="t-salesDocInfoValue">{item.value}</span>
        </div>
      ))}
    </div>
  );

  renderUploadedDocuments = (documents) => {
    if (documents.length === 0) {
      return <div className="t-salesDocDocumentsEmpty">Yüklenen doküman bulunmuyor.</div>;
    }

    return (
      <>
        <div className="t-myDemands-desktopOnly t-salesDocDocumentsTableWrap">
          <table className="table table-sm t-tablo mt-3 mb-0 t-salesDocDocumentsTable">
            <thead>
              <tr>
                <th>Doküman Adı</th>
                <th>Dosya Adı</th>
                <th>Yüklenme Tarihi</th>
                <th>Yüklendiği Yer</th>
                <th>Durum</th>
                <th className="text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((document, index) => {
                const documentName = this.getField(document, "documentName", "DocumentName");
                const fileName = this.getField(document, "fileName", "FileName");
                const uploadedDate = this.getField(document, "uploadedDate", "UploadedDate");
                const sourceText = this.getField(document, "sourceText", "SourceText");
                const status = this.getField(document, "status", "Status");
                const statusColorCode = this.getField(document, "statusColorCode", "StatusColorCode");
                const canView = this.getField(document, "canView", "CanView");

                return (
                  <tr key={this.getDocumentRowKey(document, index)}>
                    <td>{documentName || "-"}</td>
                    <td>{fileName || "-"}</td>
                    <td>{this.formatDateTime(uploadedDate)}</td>
                    <td>{sourceText || "-"}</td>
                    <td>{this.renderStatusBadge(status, statusColorCode, { fallback: null })}</td>
                    <td className="text-right">
                      <button
                        type="button"
                        className="t-salesDocViewBtn"
                        onClick={() => this.downloadDocument(document)}
                        disabled={canView === false}
                      >
                        <i className="t-icon-remove_red_eye" aria-hidden="true" />
                        Görüntüle
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="t-myDemands-mobileOnly t-myDemands-mobileList t-salesDocDocumentsMobileList">
          {documents.map((document, index) => {
            const documentName = this.getField(document, "documentName", "DocumentName");
            const fileName = this.getField(document, "fileName", "FileName");
            const uploadedDate = this.getField(document, "uploadedDate", "UploadedDate");
            const sourceText = this.getField(document, "sourceText", "SourceText");
            const status = this.getField(document, "status", "Status");
            const statusColorCode = this.getField(document, "statusColorCode", "StatusColorCode");
            const canView = this.getField(document, "canView", "CanView");

            return (
              <article className="t-myDemands-mobileCard t-salesDocDocumentCard" key={this.getDocumentRowKey(document, index)}>
                <div className="t-myDemands-mobileField">
                  <span className="t-myDemands-mobileLabel">Doküman Adı</span>
                  <span className="t-myDemands-mobileValue">{documentName || "-"}</span>
                </div>
                <div className="t-myDemands-mobileField">
                  <span className="t-myDemands-mobileLabel">Dosya Adı</span>
                  <span className="t-myDemands-mobileValue">{fileName || "-"}</span>
                </div>
                <div className="t-myDemands-mobileField">
                  <span className="t-myDemands-mobileLabel">Yüklenme Tarihi</span>
                  <span className="t-myDemands-mobileValue">{this.formatDateTime(uploadedDate)}</span>
                </div>
                <div className="t-myDemands-mobileField">
                  <span className="t-myDemands-mobileLabel">Yüklendiği Yer</span>
                  <span className="t-myDemands-mobileValue">{sourceText || "-"}</span>
                </div>
                <div className="t-myDemands-mobileField">
                  <span className="t-myDemands-mobileLabel">Durum</span>
                  <span className="t-myDemands-mobileValue">
                    {this.renderStatusBadge(status, statusColorCode, { fallback: null })}
                  </span>
                </div>
                <div className="t-myDemands-mobileActions">
                  <button
                    type="button"
                    className="t-salesDocViewBtn t-salesDocViewBtn--mobile"
                    onClick={() => this.downloadDocument(document)}
                    disabled={canView === false}
                  >
                    <i className="t-icon-remove_red_eye" aria-hidden="true" />
                    Görüntüle
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </>
    );
  };

  renderUploadDocumentRow = (definition, index) => {
    const { uploadForm, dragOverField, uploadSubmitting, uploadDetailLoading } = this.state;
    const selectedFile = uploadForm[definition.field];
    const existingDocument = this.getExistingUploadDocument(definition);
    const canUpload = definition.canUpload !== false;
    const displayName = this.getUploadDocumentDisplayName(definition);
    const hasDocument = this.hasUploadDocument(definition);
    const isExistingOnly = !selectedFile && Boolean(existingDocument);
    const inputRef =
      definition.ref === "salesContractInputRef"
        ? this.salesContractInputRef
        : this.registrationInputRef;
    const isDisabled = uploadSubmitting || uploadDetailLoading || !canUpload;
    const selectButtonLabel = existingDocument ? "Dosya Değiştir" : "Dosya Seç";
    const documentStatus = this.getField(existingDocument, "status", "Status");
    const documentStatusColorCode = this.getField(existingDocument, "statusColorCode", "StatusColorCode");
    const readOnlyDisplayName =
      displayName ||
      this.getField(existingDocument, "fileName", "FileName") ||
      this.getField(existingDocument, "documentName", "DocumentName") ||
      definition.name;

    return (
      <div
        className={`t-salesDocUploadRow ${canUpload ? "" : "t-salesDocUploadRow--readonly"}`}
        key={definition.rowKey}
      >
        <div className="t-salesDocUploadRow-title">
          <span className="t-salesDocUploadRow-index">{index + 1}</span>
          <span>
            {definition.name}
            {canUpload ? <span className="text-danger ml-1">*</span> : null}
          </span>
        </div>
        <div className="t-salesDocUploadRow-content">
          {canUpload ? (
            <div
              className={`t-salesDocDropzone ${dragOverField === definition.field ? "is-dragover" : ""} ${
                isDisabled ? "is-disabled" : ""
              }`}
              onDragEnter={(event) => this.handleDropzoneDragEnter(definition.field, event)}
              onDragOver={this.handleDropzoneDragOver}
              onDragLeave={(event) => this.handleDropzoneDragLeave(definition.field, event)}
              onDrop={(event) => this.handleDropzoneDrop(definition.field, event)}
              onClick={() => this.handleDropzoneClick(inputRef)}
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              aria-disabled={isDisabled}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  this.handleDropzoneClick(inputRef);
                }
              }}
            >
              <i className="t-icon-file_upload" aria-hidden="true" />
              <p>Dosyanızı sürükleyip bırakın veya seçin</p>
              <small>PDF, JPG, PNG (Maks. 8 MB)</small>
            </div>
          ) : null}
          <div className="t-salesDocUploadedFile">
            <strong>{isExistingOnly || !canUpload ? "Yüklü Doküman" : "Yüklenen Doküman"}</strong>
            {readOnlyDisplayName ? (
              <>
                <div className="t-salesDocUploadedFile-name">
                  <i className="t-icon-file_upload" aria-hidden="true" />
                  <span>{readOnlyDisplayName}</span>
                </div>
                {selectedFile ? <small>{this.formatFileSize(selectedFile.size)}</small> : null}
              </>
            ) : (
              <span className="text-muted">Dosya seçilmedi</span>
            )}
            {!canUpload && documentStatus ? (
              <span className="mt-2 d-inline-block">
                {this.renderStatusBadge(documentStatus, documentStatusColorCode)}
              </span>
            ) : (
              <span
                className={`t-salesDocStatus mt-2 ${
                  hasDocument
                    ? selectedFile
                      ? "t-salesDocStatus--review"
                      : "t-salesDocStatus--success"
                    : "t-salesDocStatus--default"
                }`}
              >
                {selectedFile ? "Seçildi" : hasDocument ? "Yüklendi" : "Yüklenmedi"}
              </span>
            )}
          </div>
          {canUpload ? (
            <div className="t-salesDocUploadActions">
              <button
                type="button"
                className="t-salesDocActionBtn t-salesDocActionBtn--select"
                onClick={() => inputRef.current?.click()}
                disabled={isDisabled}
              >
                <i className="t-icon-upload" aria-hidden="true" />
                {selectButtonLabel}
              </button>
              <button
                type="button"
                className="t-salesDocActionBtn t-salesDocActionBtn--clear"
                onClick={() => this.clearSelectedFile(definition.field)}
                disabled={!selectedFile || isDisabled}
              >
                <i className="t-icon-trash" aria-hidden="true" />
                Temizle
              </button>
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                style={{ display: "none" }}
                onChange={(event) => this.handleFileInputChange(definition.field, event)}
                disabled={isDisabled}
              />
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  renderDetailModal = () => {
    const { showDetailModal, selectedRequest, requestDetail, detailLoading } = this.state;
    if (!showDetailModal) return null;

    const requestId = this.getField(selectedRequest, "requestId", "RequestId");
    const plate = this.getField(selectedRequest, "plate", "Plate");
    const requestStatus =
      this.getField(requestDetail, "requestStatus", "RequestStatus") ||
      this.getRequestStatusText(selectedRequest);
    const requestStatusColorCode = this.getRequestStatusColorCode(requestDetail, selectedRequest);
    const changePlate = this.getField(requestDetail, "changePlate", "ChangePlate");
    const newPlate = this.getField(requestDetail, "newPlate", "NewPlate");
    const buyerSerial = this.getField(
      requestDetail,
      "buyerRegistrationSerialNumber",
      "BuyerRegistrationSerialNumber"
    );
    const isZeroVehicle = this.isZeroVehicleRequest(requestDetail || selectedRequest);
    const documents = this.normalizeDocuments(requestDetail);

    return (
      <>
        <div className="modal fade show t-salesDocModal" style={{ display: "block" }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
            <div className="modal-content t-guarantorModal-content">
              <div className="t-guarantorModal-header">
                <div className="t-guarantorModal-header-content">
                  <div className="t-guarantorModal-icon t-salesDocModal-icon--detail">
                    <i className="t-icon-file_upload" aria-hidden="true" />
                  </div>
                  <div>
                    <h5 className="t-guarantorModal-title">Satış Dokümanı Talep Detayı</h5>
                    <p className="t-guarantorModal-subtitle">
                      Oluşturulan talebin detay bilgilerini aşağıda görüntüleyebilirsiniz.
                    </p>
                  </div>
                </div>
                <button type="button" className="t-guarantorModal-close" onClick={this.closeDetailModal}>
                  &times;
                </button>
              </div>

              <div className="t-guarantorModal-body t-salesDocModal-body">
                {detailLoading ? (
                  this.renderLoading("Detay bilgileri hazırlanıyor, lütfen bekleyiniz.")
                ) : (
                  <>
                    <div className="t-deliverySectionCard">
                      <strong className="t-deliverySectionTitle">Talep Bilgileri</strong>
                      {this.renderInfoGrid([
                        { label: "Talep ID", value: requestId || "-" },
                        { label: "Plaka", value: plate || "-" },
                        { label: "Sıfır Araç Mı?", value: isZeroVehicle ? "Evet" : "Hayır" },
                        { label: "Talep Durum", value: this.renderStatusBadge(requestStatus, requestStatusColorCode) },
                      ])}
                    </div>

                    <div className="t-deliverySectionCard">
                      <strong className="t-deliverySectionTitle">Doldurulan Form Bilgileri</strong>
                      {this.renderInfoGrid([
                        {
                          label: "Plaka Değişti Mi?",
                          value: changePlate ? "Evet" : "Hayır",
                        },
                        { label: "Yeni Plaka", value: newPlate || "-" },
                        {
                          label: "Alıcı Ruhsat Seri Belge Numarası",
                          value: buyerSerial || "-",
                        },
                      ])}
                    </div>

                    <div className="t-deliverySectionCard">
                      <strong className="t-deliverySectionTitle">Yüklenen Dokümanlar</strong>
                      {this.renderUploadedDocuments(documents)}
                    </div>

                    <div className="t-salesDocInfoBanner">
                      <i className="t-icon-info_outline" aria-hidden="true" />
                      <span>Dokümanları görüntülemek için &apos;Görüntüle&apos; butonuna tıklayınız.</span>
                    </div>
                  </>
                )}
              </div>

              <div className="t-guarantorModal-footer t-salesDocModal-footerCenter">
                <button type="button" className="t-icButtonYesil" onClick={this.closeDetailModal}>
                  <span>Kapat</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
      </>
    );
  };

  renderUploadModal = () => {
    const {
      showUploadModal,
      selectedRequest,
      uploadForm,
      uploadSubmitting,
      uploadDetailLoading,
      uploadRequestDetail,
    } = this.state;
    if (!showUploadModal || !selectedRequest) return null;

    const requestId = this.getField(selectedRequest, "requestId", "RequestId");
    const plate = this.getField(selectedRequest, "plate", "Plate");
    const requestStatus =
      this.getField(uploadRequestDetail, "requestStatus", "RequestStatus") ||
      this.getRequestStatusText(selectedRequest);
    const requestStatusColorCode = this.getRequestStatusColorCode(uploadRequestDetail, selectedRequest);
    const isFormDisabled = uploadSubmitting || uploadDetailLoading;
    const uploadDocumentDefinitions = this.getUploadDocumentDefinitions(selectedRequest, uploadRequestDetail);
    const isZeroVehicle = this.isZeroVehicleRequest(uploadRequestDetail || selectedRequest);
    const isNewPlateRequired = this.isNewPlateRequired(uploadRequestDetail || selectedRequest, uploadForm);
    const canSubmitUpload = uploadDocumentDefinitions.some((definition) => definition.canUpload !== false);
    const isVehicleInfoLocked = this.isVehicleInfoLocked(uploadRequestDetail, selectedRequest);
    const showRejectedDocumentNotice = this.shouldShowRejectedDocumentUploadNotice(
      uploadRequestDetail,
      selectedRequest
    );

    return (
      <>
        <div className="modal fade show t-salesDocModal" style={{ display: "block" }} tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
            <div className="modal-content t-guarantorModal-content">
              <div className="t-guarantorModal-header">
                <div className="t-guarantorModal-header-content">
                  <div className="t-guarantorModal-icon t-salesDocModal-icon--upload">
                    <i className="t-icon-file_upload" aria-hidden="true" />
                  </div>
                  <div>
                    <h5 className="t-guarantorModal-title">Satış Dokümanı Yükle</h5>
                    <p className="t-guarantorModal-subtitle">
                      Oluşturulan satış dokümanı talebinin bilgilerini girerek zorunlu dokümanları yükleyiniz.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="t-guarantorModal-close"
                  onClick={this.closeUploadModal}
                  disabled={uploadSubmitting}
                >
                  &times;
                </button>
              </div>

              <div className="t-guarantorModal-body t-salesDocModal-body">
                {uploadDetailLoading ? (
                  this.renderLoading("Talep detay bilgileri hazırlanıyor, lütfen bekleyiniz.")
                ) : (
                  <>
                    <div className="t-deliverySectionCard">
                      <strong className="t-deliverySectionTitle">Talep Bilgileri</strong>
                      {this.renderInfoGrid([
                        { label: "Talep ID", value: requestId || "-" },
                        { label: "Plaka", value: plate || "-" },
                        { label: "Sıfır Araç Mı?", value: isZeroVehicle ? "Evet" : "Hayır" },
                        { label: "Talep Durum", value: this.renderStatusBadge(requestStatus, requestStatusColorCode) },
                      ])}
                    </div>

                    <div className="t-deliverySectionCard">
                      <strong className="t-deliverySectionTitle">Araç Bilgileri</strong>
                      <div className="t-salesDocFormGrid">
                        <div className="t-salesDocFormGroup">
                          <label>Plaka Değişti Mi?</label>
                          <div className="t-salesDocToggleGroup">
                            <button
                              type="button"
                              className={`t-salesDocToggleBtn ${uploadForm.changePlate || isZeroVehicle ? "is-active" : ""}`}
                              onClick={() => this.handleUploadFormChange("changePlate", true)}
                              disabled={isFormDisabled || isVehicleInfoLocked}
                            >
                              Evet
                            </button>
                            <button
                              type="button"
                              className={`t-salesDocToggleBtn ${!uploadForm.changePlate && !isZeroVehicle ? "is-active" : ""}`}
                              onClick={() => this.handleUploadFormChange("changePlate", false)}
                              disabled={isFormDisabled || isZeroVehicle || isVehicleInfoLocked}
                            >
                              Hayır
                            </button>
                          </div>
                        </div>
                        <div className="t-salesDocFormGroup">
                          <label htmlFor="salesDocNewPlate">
                            Yeni Plaka
                            {isNewPlateRequired ? <span className="text-danger ml-1">*</span> : null}
                          </label>
                          <input
                            id="salesDocNewPlate"
                            type="text"
                            className="form-control t-salesDocPlateInput"
                            placeholder="Örn. 34 ABC 123"
                            value={uploadForm.newPlate}
                            onChange={(event) => this.handleUploadFormChange("newPlate", event.target.value)}
                            disabled={
                              (!uploadForm.changePlate && !isZeroVehicle) || isFormDisabled || isVehicleInfoLocked
                            }
                            required={isNewPlateRequired}
                            maxLength={11}
                            autoComplete="off"
                            inputMode="text"
                          />
                        </div>
                        <div className="t-salesDocFormGroup t-salesDocFormGroup--full">
                          <label htmlFor="salesDocBuyerSerial">
                            Alıcı Ruhsat Seri Belge Numarası
                            <span className="text-danger ml-1">*</span>
                          </label>
                          <input
                            id="salesDocBuyerSerial"
                            type="text"
                            className="form-control t-salesDocPlateInput"
                            placeholder="Örn. AA 123456"
                            value={uploadForm.buyerRegistrationSerialNumber}
                            onChange={(event) =>
                              this.handleUploadFormChange("buyerRegistrationSerialNumber", event.target.value)
                            }
                            disabled={isFormDisabled || isVehicleInfoLocked}
                            maxLength={9}
                            autoComplete="off"
                            inputMode="text"
                          />
                        </div>
                      </div>
                    </div>

                    {showRejectedDocumentNotice ? (
                      <LoginPageAlert
                        messages={REJECTED_DOCUMENT_UPLOAD_NOTICE}
                        type="error"
                      />
                    ) : null}

                    {uploadDocumentDefinitions.map((definition, index) =>
                      this.renderUploadDocumentRow(definition, index)
                    )}

                    <div className="t-salesDocInfoBanner">
                      <i className="t-icon-info_outline" aria-hidden="true" />
                      <span>Yükleyeceğiniz dokümanların okunaklı, güncel ve eksiksiz olması gerekmektedir.</span>
                    </div>
                  </>
                )}
              </div>

              <div className="t-guarantorModal-footer">
                <button
                  type="button"
                  className="t-icButtonYesil"
                  onClick={this.submitUpload}
                  disabled={isFormDisabled || !canSubmitUpload}
                >
                  <span>{uploadSubmitting ? "Gönderiliyor..." : "Kaydet ve Gönder"}</span>
                  <i className="t-icon-arrow-right2" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
      </>
    );
  };

  renderPagination = (totalPages) => {
    const { currentPage } = this.state;
    if (totalPages <= 1) return null;

    return (
      <div className="t-salesDocPagination">
        <button
          type="button"
          className="t-salesDocPageBtn"
          disabled={currentPage === 1}
          onClick={() => this.setState({ currentPage: currentPage - 1 })}
        >
          ‹
        </button>
        {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
          <button
            key={page}
            type="button"
            className={`t-salesDocPageBtn ${currentPage === page ? "is-active" : ""}`}
            onClick={() => this.setState({ currentPage: page })}
          >
            {page}
          </button>
        ))}
        <button
          type="button"
          className="t-salesDocPageBtn"
          disabled={currentPage === totalPages}
          onClick={() => this.setState({ currentPage: currentPage + 1 })}
        >
          ›
        </button>
      </div>
    );
  };

  render() {
    const { requests, isLoading, currentPage, hasLoaded, listMeta } = this.state;
    const totalPages = Math.max(1, Math.ceil(requests.length / PAGE_SIZE));
    const pageItems = requests.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    return (
      <div className="t-salesDocTab">
        <div className="t-salesDocTab-header">
          <div className="t-salesDocTab-headerIcon">
            <i className="t-icon-files-empty" aria-hidden="true" />
          </div>
          <div>
            <h4 className="t-salesDocTab-title">Satış Dokümanı Talep Listesi</h4>
            <p className="t-salesDocTab-subtitle">
              Oluşturulan satış dokümanı taleplerinizi aşağıda görüntüleyebilirsiniz.
            </p>
          </div>
        </div>

        {!hasLoaded && !isLoading ? null : isLoading ? (
          this.renderLoading()
        ) : (
          <>
            {this.renderRequestList(pageItems, listMeta)}

            <div className="t-salesDocTableFooter">
              <span>Toplam {requests.length} kayıt</span>
              {this.renderPagination(totalPages)}
            </div>
          </>
        )}

        {this.renderDetailModal()}
        {this.renderUploadModal()}
      </div>
    );
  }
}

export default SalesDocumentUploadTab;
